import { Component, Input, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material';
import { TranslocoService } from '@ngneat/transloco';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, Subject } from 'rxjs';
import { exhaustMap, filter, take } from 'rxjs/operators';
import { Assessment } from 'src/API';
import { AssessmentService } from 'src/app/core/api/assessment.service';
import { PatientService } from 'src/app/core/api/patient.service';
import { StaffPermissionsService } from 'src/app/core/services/staff-permissions.service';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import { SymptomModes } from 'src/app/shared/symptoms/symptom.model';
import { LinkQuestionnaireModalComponent } from '../../dry-eye-medical-history-form/link-assessment-to-questionnaire-modal/link-assessment-to-questionnaire-modal.component';
import { DryEyeFormGroup } from '../dry-eye-form.model';
import { LayoutsService } from './../../../../../logged-in-navbar/clinic-setup-modal/layouts/layouts.service';

@Component({
  selector: 'csi-dry-eye-assessment-method-form',
  templateUrl: './dry-eye-assessment-method-form.component.html',
  styleUrls: ['./dry-eye-assessment-method-form.component.scss']
})
export class DryEyeAssessmentMethodFormComponent implements OnDestroy {
  public readonly SymptomModes = SymptomModes;

  @Input() formGroup: DryEyeFormGroup;
  @Input() onQuestionnaireLinked$: Subject<void>;
  @Input() saveFn: () => Observable<Assessment>;

  constructor(
    public layoutsService: LayoutsService,
    public confirmModalService: ConfirmModalService,
    public assessmentService: AssessmentService,
    public patientService: PatientService,
    public dialog: MatDialog,
    private translocoService: TranslocoService,
    private staffPermissionsService: StaffPermissionsService
  ) {}

  get assessmentMethod(): SymptomModes {
    return this.formGroup.controls.assessmentMethod.value;
  }

  noSort() {
    return 0;
  }

  ngOnDestroy(): void {}

  openLinkQuestionnaireModal() {
    if (!this.staffPermissionsService.permissions.canLinkQuestionnaire) {
      return;
    }
    const title = this.translocoService.translate('Questionnaires Available for Linking');
    const body = this.translocoService.translate(`You have questionnaires available for linking. Would you like to view them?`);
    const yesButton = this.translocoService.translate('View Questionnaires');
    const noButton = this.translocoService.translate('Cancel');
    this.confirmModalService
      .show(
        title,
        body,
        yesButton,
        noButton
      )
      .pipe(
        untilDestroyed(this),
        filter(Boolean),
        exhaustMap(() => {
          const dialogRef = this.dialog.open(LinkQuestionnaireModalComponent, {
            data: { saveFn: this.saveFn }
          });

          return dialogRef.afterClosed().pipe(take(1));
        })
      )
      .subscribe(newlyLinkedQuestionnaires => {
        this.onQuestionnaireLinked$.next(newlyLinkedQuestionnaires);
      });
  }

  onMethodChange(newMethod: SymptomModes) {
    const currentAssessmentMethod = this.formGroup.controls.assessmentMethod.value;
    const currentFriendlyMethod = this.layoutsService.assessmentMethodFriendlyName[
      currentAssessmentMethod
    ];
    const newFriendlyMethod = this.layoutsService.assessmentMethodFriendlyName[newMethod];

    if (!currentAssessmentMethod) {
      this.formGroup.controls.assessmentMethod.setValue(newMethod);

      // DISPLAY LINK QUESTIONNAIRE MODAL FOR NEW ASSESSMENTS
      if (this.patientService.questionnaires.length > 0) {
        setTimeout(() => {
          this.openLinkQuestionnaireModal();
        }, 1000);
      }
      return;
    }

    if (
      (currentAssessmentMethod !== SymptomModes.PreOp && newMethod === SymptomModes.PreOp) ||
      (currentAssessmentMethod === SymptomModes.PreOp && newMethod !== SymptomModes.PreOp)
    ) {
      const title = this.translocoService.translate(
        'dryEyeAssessmentMethodForm.changeMethodTitle',
        { current: currentFriendlyMethod, new: newFriendlyMethod }
      );

      const body = this.translocoService.translate('dryEyeAssessmentMethodForm.changeMethodBody', {
        current: currentFriendlyMethod,
        new: newFriendlyMethod
      });

      const yesButton = this.translocoService.translate(
        'dryEyeAssessmentMethodForm.overwriteAndChange'
      );
      const noButton = this.translocoService.translate('dryEyeAssessmentMethodForm.cancel');

      return this.confirmModalService
        .show(title, body, yesButton, noButton)
        .subscribe(confirmed => {
          if (confirmed) {
            this.formGroup.controls.assessmentMethod.setValue(newMethod);
          }
        });
    }

    this.formGroup.controls.assessmentMethod.setValue(newMethod);
  }
}
